import React, { useState, Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { get, find } from 'lodash';
import Loading from 'react-loading-components';
import { Badge } from 'reactstrap';
import Notification from 'react-notification-system';
import Dropzone from 'react-dropzone';
import trainingcenter from 'assets/images/training-center.png';
import Modal from 'components/Modal/AnimatedModal';
import Header from 'components/Header';
import { ButtonLink, Button } from 'components/Button';
import { Grid, Column } from 'components/Layout';
import Input from 'components/Form/Input';
import { formatCurrency } from 'utils/formatCurrency';

import { getStats, getApplications } from 'actions/application';
import { importProspectApplications, importProspectApplicationsId } from 'actions/prospect';
import { downloadCSV } from 'components/CSVExport';
import refund from 'assets/icons/refund.svg';
import resources from 'assets/icons/merchant-resources.svg';

import checklistBlue from 'assets/icons/checklist-blue.svg';
import checklistNoun from 'assets/icons/checklist.svg';
import cloudUpload from 'assets/icons/cloud-upload.svg';
import actionRequired from 'assets/icons/action-required-alert.svg';
import style from './style.scss';

let importApplicationsTimer;

const Dashboard = (props) => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState('');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionRequired, setIsActionRequired] = useState(false);
  const [dropzoneActive, setDropzoneActive] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [prospectResults, setProspectResults] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [showInfoPopup, setShowInfoPopup] = useState(false);

  const dropzoneRef = useRef(null);
  const notification = useRef(null);

  const isSuspendedMerchant = localStorage.getItem('merchantStatus') === '1012';
  const enabledPreSelection = get(
    find(
      JSON.parse(localStorage.getItem('features')),
      { code: 'preselection' }
    ),
    'value'
  ) === 'Enabled';
  const isProspectUploadEnabled = get(
    find(
      JSON.parse(localStorage.getItem('features')),
      { code: 'prospectupload' }
    ),
    'value'
  ) === 'Enabled';

  const handleKeydown = (event) => {
    if (event.keyCode === 13) {
      props.history.push(`/dashboard/application-review/action/AllApplications/${searchQuery}`);
    }
  };

  const closePopup = () => {
    localStorage.setItem('merchantInfoPopup', false);
    setShowInfoPopup(false);
  };

  useEffect(() => {
    const merchantInfoPopup = localStorage.getItem('merchantInfoPopup');
    setShowInfoPopup(merchantInfoPopup === 'true');
  }, [data]);

  useEffect(() => {
    const merchantId = localStorage.getItem('merchantId');

    dispatch(getStats({
      url: `/stats/merchant/${merchantId}/aggregate`,
      success: (res) => {
        setData({
          needFundingForm: get(
            find(get(res, 'aggregates'), { code: 'NeedFundingForm' }),
            'value'
          ),
          needCustomerDedicatedAccount: get(
            find(get(res, 'aggregates'), { code: 'NeedCustomerDedicatedAccount' }),
            'value'
          ),
          appsSubmittedMTD: get(
            find(get(res, 'aggregates'), { code: 'AppsSubmittedMTD' }),
            'value'
          ),
          appsSubmittedYTD: get(
            find(get(res, 'aggregates'), { code: 'AppsSubmittedYTD' }),
            'value'
          ),
          loansApproved: get(
            find(get(res, 'aggregates'), { code: 'LoansApproved' }),
            'value'
          ),
          needImmediateCertificateOfSettlement: get(
            find(get(res, 'aggregates'), { code: 'NeedImmediateCertificateOfSettlement' }),
            'value'
          ),
          needMediumCertificateOfSettlement: get(
            find(get(res, 'aggregates'), { code: 'NeedMediumCertificateOfSettlement' }),
            'value'
          ),
          needLowCertificateOfSettlement: get(
            find(get(res, 'aggregates'), { code: 'NeedLowCertificateOfSettlement' }),
            'value'
          ),
          counterOffer: get(
            find(get(res, 'aggregates'), { code: 'CounterOffer' }),
            'value'
          ),
        });
        setIsLoading(false);
      },
      fail: () => {},
    }));

    dispatch(getApplications({
      url: `applications-view/search?merchantId=${merchantId}&channelCode=fsl&DaysFromSettlementsFunded.End=-90&sortBy=applicationDate&sortOrder=desc&&offset=0&limit=1`,
      success: (res) => {
        setIsActionRequired(!!get(res, 'data.0'));
      },
    }));
  }, []);

  const onDragEnter = () => {
    setDropzoneActive(true);
  };

  const onDragLeave = () => {
    setDropzoneActive(false);
  };

  const handleError = (err) => {
    if (err.data && err.data.message) {
      notification.current
      && notification.current.addNotification({
        message: err.data.message,
        level: 'error',
        position: 'tc',
      });
    } else {
      console.log('Error', err);
    }
  };

  const onDrop = (files) => {
    setDropzoneActive(false);
    setIsRunning(true);

    const merchantId = localStorage.getItem('merchantId');
    files.forEach((file) => {
      const formData = new FormData();

      formData.append('file', file);
      formData.append('merchantId', merchantId);

      dispatch(importProspectApplicationsId({
        formData,
        success: (res) => {
          importApplicationsTimer = setInterval(() => {
            dispatch(importProspectApplications({
              id: res && res.id,
              success: (prospectAppsRes) => {
                if (!prospectAppsRes || prospectAppsRes.status !== 'Processing') {
                  clearInterval(importApplicationsTimer);
                  if (get(prospectAppsRes, 'status') === 'Failed') {
                    notification.current
                    && notification.current.addNotification({
                      message: 'Prospects upload failed.',
                      level: 'error',
                      position: 'tc',
                    });
                  } else {
                    notification.current
                    && notification.current.addNotification({
                      message: 'Prospects have been uploaded successfully!',
                      level: 'success',
                      position: 'tc',
                    });
                  }
                  setProspectResults(prospectAppsRes && prospectAppsRes.results);
                  setIsRunning(false);
                  setCompleted(true);
                }
              },
              fail: (err) => {
                clearInterval(importApplicationsTimer);
                setIsRunning(false);
                handleError(err);
              },
            }));
          }, 5000);
        },
        fail: (err) => {
          setIsRunning(false);
          handleError(err);
        },
      }));
    });
  };

  const openFileUpload = () => {
    dropzoneRef.current && dropzoneRef.current.open();
  };

  const handleClickButton = (url, e) => {
    e.preventDefault();
    props.history.push(url);
  };

  const handleChange = (e) => {
    e.preventDefault();
    setSearchQuery(e.target.value);
  };

  const handleButtonClick = (type) => {
    if (props.isApplicationReviewDataLoaded) {
      props.history.push(`/dashboard/application-review/action/${type}`);
    }
  };

  const handleDownload = (e) => {
    e.preventDefault();

    const csvRows = [];
    csvRows.push({
      applicationId: 'ApplicationId',
      settlementId: 'SettlementId',
      clientId: 'ClientId',
      fieldName: 'FieldName',
      errorMessage: 'ErrorMessage',
      value: 'Value',
      result: 'Result',
    });
    prospectResults && prospectResults.forEach(cur => csvRows.push({
      applicationId: cur.applicationId,
      settlementId: cur.settlementId,
      clientId: cur.clientId,
      fieldName: cur.fieldName,
      errorMessage: cur.errorMessage,
      value: cur.value,
      result: cur.result,
    }));
    downloadCSV(csvRows, 'prospect-report.csv');
  };

  return (
    <Fragment>
      <Notification ref={notification} />
      <Header />
      <section className={cn('grid-container fluid portal page-dashboard section', style.dashboard)}>
        <Grid>
          <Column id="StartBar" className="small-12 large-3">
            <Grid className="grid-margin-x">
              <Column
                id="StartHere"
                className={
                  cn(
                    'card small-12',
                    style['app-submission-card'],
                    { [style['disabled-area']]: isSuspendedMerchant }
                  )
                }
              >
                <h2>Start Here</h2>
                <p className="p-large">To submit applications</p>
                <ButtonLink
                  className={cn(
                    'button arrow green',
                    { [style['disabled-btn']]: isSuspendedMerchant }
                  )}
                  onClick={
                    isSuspendedMerchant
                      ? undefined
                      : handleClickButton.bind(null, '/dashboard/email-app')
                  }
                >
                  Apply Now
                </ButtonLink>
              </Column>

              <Column className="small-12">
                <Input
                  label="Check App Status"
                  name="appStatus"
                  placeHolder="Applicant Name"
                  value={searchQuery}
                  onChange={handleChange}
                  onKeyDown={handleKeydown}
                />
              </Column>
              <Column id="StartActions" className={cn('small-12', style.startActions)}>
                <ButtonLink
                  className={cn(style['hover-highlight'], style.buttonLink)}
                  onClick={props.isApplicationReviewDataLoaded ? handleClickButton.bind(null, '/dashboard/application-review/action/AllApplications') : undefined}
                >
                  <img src={checklistBlue} alt="Checklist Blue" />
                  <h5>Review Applications</h5>
                  <p className="p-small">View current and<br /> historical applications</p>
                </ButtonLink>
                {enabledPreSelection && (
                  <ButtonLink
                    className={cn(style['hover-highlight'], style.buttonLink)}
                    onClick={handleClickButton.bind(null, '/pre-selection/start')}
                  >
                    <img className={style['checklist-noun']} src={checklistNoun} alt="Checklist Noun" />
                    <h5>Start Pre-Selection Tool</h5>
                    <p className="p-small">See if your client is a good candidate for a loan</p>
                  </ButtonLink>
                )}
                <ButtonLink
                  className={cn(style['hover-highlight'], style.buttonLink)}
                  onClick={handleClickButton.bind(null, '/dashboard/application-review/action/CounterOffers')}
                >
                  <img className={style['checklist-noun']} src={checklistNoun} alt="Checklist Noun" />
                  <h5>Review Counter Offers</h5>
                  <p className="p-small">Action required: Counter offers need your approval before continuing</p>
                  <Badge pill color="danger" className={`top-right ${style['top-right-badge']}`}>
                    {get(data, 'counterOffer') || 0}
                  </Badge>
                </ButtonLink>
                {isProspectUploadEnabled && (
                  <div style={{ position: 'relative', borderBottom: 'none' }}>
                    <Dropzone
                      disableClick
                      multiple={false}
                      style={{ position: 'relative', borderBottom: 'none' }}
                      ref={dropzoneRef}
                      onDrop={onDrop}
                      onDragEnter={onDragEnter}
                      onDragLeave={onDragLeave}
                    >
                      <ButtonLink
                        className={cn(style['hover-highlight'], style.uploadProspect)}
                        onClick={openFileUpload}
                      >
                        <img className={style['cloud-upload']} src={cloudUpload} alt="Checklist Noun" />
                        {isRunning ? <Loading type="oval" width={50} height={50} fill="#f44242" /> : <h5>Upload Prospect</h5>}
                        {dropzoneActive && <div className={style['dropzone-overlay']}>Drop file here to upload CSV</div>}
                      </ButtonLink>
                    </Dropzone>
                    {completed && !isRunning && (
                      <div
                        className={style['download-prospect']}
                        onClick={handleDownload}
                      >
                        Download
                      </div>
                    )}
                  </div>
                )}
              </Column>
            </Grid>
          </Column>
          <Column className="small-12 large-9 card-grid-container">
            {
              isActionRequired &&
                <div className={style['action-required-alert']}>
                  <img src={actionRequired} alt="Action Required Alert" />
                  <strong>ACTION REQUIRED:</strong>
                  <span><small><b>The number of outstanding Certificate of Settlements exceed the acceptable tolerance level.</b></small></span>
                  <div
                    className={cn('button small', style.button)}
                    onClick={handleButtonClick.bind(null, 'NeedCertificateOfSettlement90Days')}
                  >
                    Take Action Now
                  </div>
                </div>
            }
            <Grid className="grid-margin-x">
              <Column className="small-12 large-6">
                <div id="ActionCenter" className={cn('card', style['action-center'])}>
                  <div className={cn('card-header', style.header)}>
                    <h5>Action Center</h5>
                  </div>
                  <Grid className={cn('card-grid grid-y', style.container)}>
                    <ButtonLink
                      className={cn(style['need-funding-form'], style['hover-highlight'], 'cell small-6')}
                      onClick={() => {}}
                    >
                      <h6>Need Funding Form</h6>
                      <h2 className={style['count-section']}>
                        {
                          isLoading ?
                            <Loading type="oval" width={50} height={50} fill="#f44242" />
                          :
                            get(data, 'needFundingForm') || 0
                        }
                      </h2>
                      <div
                        className={
                          cn(
                            'button green',
                            { [style['disabled-btn']]: !get(data, 'needFundingForm') }
                          )
                        }
                        onClick={
                          get(data, 'needFundingForm')
                            ? handleButtonClick.bind(null, 'NeedFundingForm')
                            : undefined
                        }
                      >
                        Take Action
                      </div>
                    </ButtonLink>
                    <ButtonLink
                      className={cn(style['customer-dedicated-account'], style['hover-highlight'], 'cell small-6')}
                      onClick={() => {}}
                    >
                      <h6>Need Customer Dedicated Account</h6>
                      <h2 className={style['count-section']}>
                        {
                          isLoading ?
                            <Loading type="oval" width={50} height={50} fill="#f44242" />
                          :
                            get(data, 'needCustomerDedicatedAccount') || 0
                        }
                      </h2>
                      <div
                        className={
                          cn(
                            'button green',
                            { [style['disabled-btn']]: !get(data, 'needCustomerDedicatedAccount') }
                          )
                        }
                        onClick={
                          get(data, 'needCustomerDedicatedAccount')
                            ? handleButtonClick.bind(null, 'NeedCustomerDedicateAccount')
                            : undefined
                        }
                      >
                        Take Action
                      </div>
                    </ButtonLink>
                    <ButtonLink
                      className={cn(style['certificate-settlement-section'], style['hover-highlight'], 'cell small-6')}
                      onClick={() => {}}
                    >
                      <h6 className={style['settlement-title']}>Need Certificate Of Settlement</h6>
                      <div className="flex-y h-100">
                        <div className={style['action-sub-content']}>
                          <b className={style['sub-content-title']}>High Priority</b>
                          <h4 className={cn({ 'text-danger': !!get(data, 'needImmediateCertificateOfSettlement') })}>
                            {
                              isLoading ?
                                <Loading type="oval" width={30} height={30} fill="#f44242" />
                              :
                                get(data, 'needImmediateCertificateOfSettlement') || 0
                            }
                          </h4>
                          <div
                            className={
                              cn(
                                'button green small',
                                { [style['disabled-btn']]: !get(data, 'needImmediateCertificateOfSettlement') }
                              )
                            }
                            onClick={
                              get(data, 'needImmediateCertificateOfSettlement')
                                ? handleButtonClick.bind(null, 'NeedCertificateOfSettlementImmediate')
                                : undefined
                            }
                          >
                            Take Action
                          </div>
                        </div>
                        <div className={style['action-sub-content']}>
                          <b className={style['sub-content-title']}>Medium Priority</b>
                          <h4 className={cn({ 'text-warning': !!get(data, 'needMediumCertificateOfSettlement') })}>
                            {
                              isLoading ?
                                <Loading type="oval" width={30} height={30} fill="#f44242" />
                              :
                                get(data, 'needMediumCertificateOfSettlement') || 0
                            }
                          </h4>
                          <div
                            className={
                              cn(
                                'button green small',
                                { [style['disabled-btn']]: !get(data, 'needMediumCertificateOfSettlement') }
                              )
                            }
                            onClick={
                              get(data, 'needMediumCertificateOfSettlement')
                                ? handleButtonClick.bind(null, 'NeedCertificateOfSettlementMedium')
                                : undefined
                            }
                          >
                            Take Action
                          </div>
                        </div>
                        <div className={style['action-sub-content']}>
                          <b className={style['sub-content-title']}>Low Priority</b>
                          <h4>
                            {
                              isLoading ?
                                <Loading type="oval" width={30} height={30} fill="#f44242" />
                              :
                                get(data, 'needLowCertificateOfSettlement') || 0
                            }
                          </h4>
                          <div
                            className={
                              cn(
                                'button green small',
                                { [style['disabled-btn']]: !get(data, 'needLowCertificateOfSettlement') }
                              )
                            }
                            onClick={
                              get(data, 'needLowCertificateOfSettlement')
                                ? handleButtonClick.bind(null, 'NeedCertificateOfSettlementLow')
                                : undefined
                            }
                          >
                            Take Action
                          </div>
                        </div>
                      </div>
                    </ButtonLink>
                  </Grid>
                </div>
              </Column>

              <Column className="small-12 large-6">

                <div id="LoanStats" className="card">
                  <div className={cn('card-header', style.header)}>
                    <h5>Loan Stats</h5>
                  </div>
                  <Grid className="card-grid">
                    <Column className="small-6">
                      <h6>Apps Submitted MTD</h6>
                      <h2>
                        {
                          isLoading ?
                            <Loading type="oval" width={50} height={50} fill="#f44242" />
                          :
                            get(data, 'appsSubmittedMTD') || '-'
                        }
                      </h2>
                    </Column>
                    <Column className="small-6">
                      <h6>Apps Submitted YTD</h6>
                      <h2>
                        {
                          isLoading ?
                            <Loading type="oval" width={50} height={50} fill="#f44242" />
                          :
                            get(data, 'appsSubmittedYTD') || '-'
                        }
                      </h2>
                    </Column>
                    <Column className="small-12">
                      <h6>Loans Approved</h6>
                      <h2>
                        {
                          isLoading ?
                            <Loading type="oval" width={50} height={50} fill="#f44242" />
                          :
                            `$${formatCurrency(get(data, 'loansApproved') || '0', 2)}`
                        }
                      </h2>
                    </Column>
                  </Grid>
                </div>

                <div id="TransactionTools" className="card">
                  <div className={cn('card-header', style.header)}>
                    <h5>Transaction Tools</h5>
                  </div>
                  <Grid className="card-grid">
                    <Column className="small-12 m-0 p-0">
                      <ButtonLink
                        className={style['card-hover-highlight']}
                        onClick={handleClickButton.bind(null, '/dashboard/freshstart')}
                      >
                        <div className="icon-bubble mr-2">
                          <img src={refund} alt="Request Refund" />
                        </div>
                        <div className={style['text-wrapper']}>
                          <h5 className="m-0">Return of Funds Review</h5>
                          <small>Start the process for a refund</small>
                        </div>
                      </ButtonLink>
                    </Column>
                  </Grid>
                </div>

                <div id="KnowledgeBase" className="card mb-0">
                  <div className={cn('card-header', style.header)}>
                    <h5>Knowledge Base</h5>
                  </div>
                  <Grid className="card-grid">
                    <Column className="small-12 m-0 p-0">
                      <ButtonLink
                        className={style['card-hover-highlight']}
                        onClick={handleClickButton.bind(null, '/trainingcenter')}
                      >
                        <div className="icon-bubble mr-2">
                          <img src={trainingcenter} alt="Request Refund" className="rounded_border" />
                        </div>
                        <div className={style['text-wrapper']}>
                          <h5 className="m-0">Training and Knowledge Center</h5>
                          <small>Read frequently asked questions or book a training with an FreshStart Lending expert!</small>
                        </div>
                      </ButtonLink>
                      <ButtonLink
                        className={style['card-hover-highlight']}
                        onClick={handleClickButton.bind(null, '/resources')}
                      >
                        <div className="icon-bubble mr-2">
                          <img src={resources} alt="Request Refund" />
                        </div>
                        <div className={style['text-wrapper']}>
                          <h5 className="m-0">Marketing Resources</h5>
                          <small>Get messaging strategies, email copy, landing pages, and more</small>
                        </div>
                      </ButtonLink>
                    </Column>
                  </Grid>
                </div>
              </Column>
            </Grid>
          </Column>
        </Grid>
      </section>
      <Modal
        isShown={showInfoPopup}
        toggle={closePopup}
        canBeClosedByEsc
        className={`sm-modal ${style['info-modal']}`}
      >
        <div className="popup-content-wrapper">
          <div className={style.head}>
            <h4>Update Your Information</h4>
          </div>
          <div className="content">
            <p>
              Welcome back!  It&apos;s been awhile.
            </p>
            <p>
              We&apos;d like to schedule a refresher on our program before you submit your next application.
            </p>
            <p>
              Please contact us at <a href="malito:fslrmteam@lendingusa.com">fslrmteam@lendingusa.com</a>.
            </p>
            <Button
              onClick={closePopup}
              className="button green"
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </Fragment>
  );
};

Dashboard.propTypes = {
  history: PropTypes.object.isRequired,
  isApplicationReviewDataLoaded: PropTypes.bool.isRequired,
};

export default withRouter(Dashboard);



// WEBPACK FOOTER //
// ./src/containers/pages/Dashboard/index.js